<template>
    <main class="account-settings-page">
        <v-container class="account-settings-page__container">
            <account-settings-header :items="links" />
            <div class="account-settings-page__grid">
                <section class="account-settings-group" aria-labelledby="account-profile-heading">
                    <header class="account-settings-group__heading">
                        <span class="account-settings-group__eyebrow">YOUR PROFILE</span>
                        <h2 id="account-profile-heading">Personal details</h2>
                        <p>Keep your contact information current so we can reach you about your service.</p>
                    </header>
                    <user-info
                        :user-stored-contact="$auth.user"
                        outlined
                        :sub="$auth.user.sub"
                        dense
                        dark
                        @save="saveInfo"
                    />
                    <div v-if="!federatedAcct" class="account-settings-group__separator"></div>
                    <reset-password v-if="!federatedAcct" outlined dense dark :user-id="$auth.user.sub" />

                    <div class="account-settings-group__separator"></div>
                    <linked-account outlined dense dark />
                </section>

                <section class="account-settings-group" aria-labelledby="account-address-heading">
                    <header class="account-settings-group__heading">
                        <span class="account-settings-group__eyebrow">DELIVERY DETAILS</span>
                        <h2 id="account-address-heading">Addresses</h2>
                        <p>Manage where invoices are sent and repaired devices should be returned.</p>
                    </header>
                    <address-form
                        v-model="oidcBilling"
                        outlined
                        dense
                        dark
                        label="Billing"
                        @save="(data) => saveInfo({ address: { billing: data } })"
                    />
                    <div class="account-settings-group__separator"></div>
                    <address-form
                        v-model="oidcShipping"
                        outlined
                        dense
                        dark
                        :copy-from="copyBilling ? oidcBilling : null"
                        :disabled="copyBilling"
                        label="Shipping"
                        @save="(data) => saveInfo({ address: { shipping: data } })"
                        @cancel="resetCopyBilling"
                    >
                        <template v-slot:copy-from-billing>
                            <v-checkbox v-model="copyBilling" hide-details dense :label="`Same as billing`" />
                        </template>
                    </address-form>
                </section>
            </div>
            <v-snackbar v-model="snackbar" color="red" :timeout="15000" top>
                {{ errorMessage }}

                <template v-slot:action="{ attrs }">
                    <v-btn dark text v-bind="attrs" @click="snackbar = false"> Close </v-btn>
                </template>
            </v-snackbar>
        </v-container>
    </main>
</template>

<script>
import UserInfo from '@/components/account/user_info'
import AddressForm from '@/components/account/address'
import ResetPassword from '@/components/account/reset_password'
import LinkedAccount from '@/components/account/linked_account'
import AccountSettingsHeader from './account_settings_header.vue'
import { navLinks } from './navigation.js'
import { postAccountDetails } from '@/api'

export default {
    metaInfo: { title: `Your Account`, subTitle: `Account Settings` },
    name: `AccountSettings`,
    components: {
        UserInfo,
        AddressForm,
        ResetPassword,
        LinkedAccount,
        AccountSettingsHeader
    },
    data() {
        return {
            links: navLinks,
            billingEdit: false,
            shippingEdit: false,
            copyBilling: true,
            errorMessage: null,
            snackbar: false
        }
    },
    computed: {
        federatedAcct() {
            const sub = this.$auth.user.sub
            return !sub.match(
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
            )
        },
        oidcBilling() {
            return this.$auth.user.address ? this.$auth.user.address.billing : {}
        },
        oidcShipping() {
            return this.$auth.user.address ? this.$auth.user.address.shipping : {}
        }
    },

    mounted() {
        this.resetCopyBilling()
    },

    methods: {
        resetCopyBilling() {
            if (this.$auth.user.address && typeof this.$auth.user.address.copyBilling !== `undefined`) {
                this.copyBilling = this.$auth.user.address.copyBilling
            }
        },
        async saveInfo(data) {
            if (data.address) {
                data.address.copyBilling = this.copyBilling
                if (data.address.shipping && this.copyBilling) {
                    data.address.shipping = this.oidcBilling
                }

                if (data.address.billing && this.copyBilling) {
                    data.address.shipping = data.address.billing
                }
            }

            try {
                await postAccountDetails(data)
                await this.$auth.loadUserProfile()
            } catch (e) {
                this.snackbar = true
                this.errorMessage = `Unable to update account. Please try again later.`
            }
        }
    }
}
</script>

<style scoped>
.account-settings-page {
    position: relative;
    isolation: isolate;
    min-height: 100vh;
    padding: 34px 0 54px;
    color: #f2f4fa;
    background: radial-gradient(ellipse at 88% 0, rgba(219, 81, 32, 0.15), transparent 38rem),
        linear-gradient(145deg, #292c35, #22252d 65%, #2a2d35);
    font-family: 'Work Sans', 'Roboto', sans-serif;
}
.account-settings-page::before {
    position: fixed;
    z-index: 0;
    inset: 0;
    background-image: url('https://storage.googleapis.com/gusa-assets/army-BG3.png');
    background-position: center;
    background-size: cover;
    content: '';
    filter: grayscale(1) brightness(0.8);
    mix-blend-mode: soft-light;
    opacity: 0.1;
    pointer-events: none;
}
.account-settings-page__container {
    position: relative;
    z-index: 1;
    max-width: 1220px;
}
.account-settings-page__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: start;
    gap: 20px;
}
.account-settings-group {
    min-width: 0;
    padding: 21px;
    background: linear-gradient(145deg, rgba(52, 55, 65, 0.91), rgba(37, 40, 49, 0.96));
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 13px;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.14), inset 0 1px 0 rgba(255, 255, 255, 0.035);
}
.account-settings-group__heading {
    margin-bottom: 16px;
    padding: 0 2px 14px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.11);
}
.account-settings-group__eyebrow {
    color: #f2a58b;
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 1.35px;
}
.account-settings-group__heading h2 {
    margin: 5px 0 4px;
    color: #f2f4fa;
    font-size: 18px;
    font-weight: 600;
    letter-spacing: -0.25px;
}
.account-settings-group__heading p {
    margin: 0;
    color: #aeb5c3;
    font-size: 11px;
    line-height: 1.55;
}
.account-settings-group__separator {
    height: 1px;
    margin: 15px 0;
    background: rgba(255, 255, 255, 0.09);
}
.account-settings-group ::v-deep .v-card {
    color: #f1f2f5;
    background: rgba(23, 25, 31, 0.38);
    border-color: rgba(255, 255, 255, 0.1) !important;
    border-radius: 9px;
    box-shadow: none !important;
}
.account-settings-group ::v-deep .v-card__title {
    color: #f2f4fa;
    font-size: 15px;
    font-weight: 600;
}
.account-settings-group ::v-deep .v-card__text {
    color: #d8dbe3;
}
.account-settings-group ::v-deep .v-input,
.account-settings-group ::v-deep .v-label,
.account-settings-group ::v-deep .v-icon {
    color: #c6cbd5;
}
.account-settings-group ::v-deep .v-input input,
.account-settings-group ::v-deep .v-input textarea {
    color: #f1f2f5;
}
.account-settings-group ::v-deep .v-input--is-disabled input {
    color: #9299a8;
}
.account-settings-group ::v-deep .v-input--is-focused .v-label,
.account-settings-group ::v-deep .v-input--is-focused .v-icon {
    color: #ff9a74;
}
.account-settings-page a:focus-visible,
.account-settings-page button:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 3px;
}
@media (max-width: 900px) {
    .account-settings-page__grid {
        grid-template-columns: minmax(0, 1fr);
    }
}
@media (max-width: 600px) {
    .account-settings-page {
        padding-top: 20px;
    }
    .account-settings-group {
        padding: 15px;
    }
}
</style>
